/**
 * The last line of defence. Any render error anywhere below this used to take
 * the whole page down to a white screen with nothing to click and nothing to
 * report — the user only sees "it doesn't load" and we only see a minified
 * stack in the error panel.
 *
 * Now the crash is caught, explained in plain language, logged with the real
 * message, and the person gets two ways out: reload, or sign out and back in.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logPortalError } from '@/integrations/errorLog';

type Props = { children: ReactNode; onSignOut?: () => void };
type State = { error: Error | null };

/** React's production build ships numbered errors; spell out the common ones
 *  so the panel says what happened instead of a decoder URL. */
function explain(err: Error): string {
  const m = /Minified React error #(\d+)/.exec(err.message);
  if (!m) return err.message;
  switch (m[1]) {
    case '185': return 'Render loop: a component kept updating itself without settling (React #185).';
    case '130': return 'A component rendered as undefined — usually a missing or broken import (React #130).';
    case '31': return 'An object was rendered where text was expected (React #31).';
    case '321': return 'A hook ran outside a component, or hooks changed order between renders (React #321).';
    case '310': return 'A component rendered a different set of hooks than on its previous render (React #310).';
    case '300': return 'A component rendered more hooks than on its previous render (React #300).';
    case '418':
    case '423': return 'The server-rendered markup did not match the browser (React hydration).';
    default: return `React error #${m[1]}: ${err.message}`;
  }
}

export class CrashGuard extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    void logPortalError({
      kind: 'runtime',
      message: `Screen crashed: ${explain(error)}`,
      context: window.location.href,
      stack: `${error.stack || error.message}\n\nComponent stack:${info.componentStack || ''}`,
    }).catch(() => undefined);
  }

  render() {
    const { error } = this.state;
    if (!error) {
      /* Una pantalla que se pinta entera borra la cuenta: el bucle terminó. */
      try { sessionStorage.removeItem('lexidy-crash-loops'); } catch { /* da igual */ }
      return this.props.children;
    }
    /* ── «LE DOY A RECARGAR Y NO PASA NADA» ──
       Los botones sí funcionaban: la pantalla se volvía a caer al instante,
       y desde fuera eso es idéntico a un botón muerto. Contamos las caídas
       en esta pestaña; a partir de la segunda se dice en voz alta que está
       en bucle y que recargar no va a servir. Un bucle silencioso es lo que
       hace que alguien se pase veinte minutos pulsando. */
    let loops = 0;
    try {
      loops = Number(sessionStorage.getItem('lexidy-crash-loops') || '0');
      sessionStorage.setItem('lexidy-crash-loops', String(loops + 1));
    } catch { /* private mode: sin cuenta, pero la pantalla sale igual */ }
    const looping = loops >= 1;
    return (
      <div className="crash">
        <div className="crash-card">
          {/* ── EN INGLÉS, PARA TODO EL MUNDO ──
              Esta pantalla la escribí en español, y la ve un equipo repartido
              entre España, Francia, Portugal, Italia, Grecia y México. A
              Victoire, que trabaja en francés, le salió tres veces seguidas
              en un idioma que no es el suyo ni el de la casa. El inglés es lo
              que todos comparten aquí, y además es el idioma del propio
              mensaje de React que va justo debajo: mezclar los dos hacía la
              tarjeta más difícil de leer, no más cercana.
              Y hay una razón de fondo: lo que se pega o se cuenta al equipo
              de Producto se busca luego en un registro escrito en inglés. */}
          <h1>This screen crashed</h1>
          {/* SAY WHAT HAPPENED. The reader was being told a fault existed and
              then shown a collapsed "Technical detail" that, on a numbered
              React error, said nothing they could use. The plain sentence is
              the headline now; the raw message stays underneath for whoever
              wants it. */}
          <p className="crash-what">{explain(error)}</p>
          <p className="crash-sub">
            It has been logged in full, so there is nothing you need to copy.
            If it happens again, tell the Product team — the rough time is enough.
          </p>
          {looping && (
            <p className="crash-loop">
              ⚠ This has now crashed {loops + 1} times in this tab. Reloading will
              not help — use <b>Reset this browser</b>, which clears what this browser
              remembered without signing you out or losing your country.
            </p>
          )}
          {/* THREE WAYS OUT, WEAKEST FIRST — and the strong one exists because
              reloading and going home both failed for the person who hit this:
              the crash followed her to every page, so it is not the page. What
              a page cannot escape is what this browser has REMEMBERED. Reset
              clears everything the portal stored here — the remembered
              section, filters, snapshots, drafts of preferences — and starts
              over. It deliberately leaves the Supabase session alone, so it
              costs a fresh sign-in only if you then choose one. */}
          <div className="crash-actions">
            <button className="btn btn-sm" onClick={() => window.location.reload()}>Reload</button>
            <button className="btn btn-sm btn-ghost" onClick={() => window.location.assign('/home')}>Go to the start</button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => {
                try {
                  /* ── NO BORRAR EL PAÍS: ERA LO QUE ALIMENTABA EL BUCLE ──
                     Este botón limpiaba TODAS las claves `lexidy-`, incluidas
                     `lexidy-kb-country` y `lexidy-kb-dept`. Sin país, la
                     pantalla vuelve a pasar por la puerta de bienvenida — que
                     es exactamente el render donde estaba el fallo #310. O
                     sea: el botón que existía para salir del bucle era el que
                     lo garantizaba. Victoire lo pulsó y volvió a caerse.
                     El fallo ya no está, pero la regla se queda: quién eres y
                     dónde trabajas no es basura de navegador. Se borra lo que
                     se puede volver a calcular —filtros, pestañas recordadas,
                     copias de seguridad, borradores— y nada más. */
                  const KEEP = ['lexidy-kb-country', 'lexidy-kb-dept', 'lexidy-kb-lang', 'lexidy-kb-office'];
                  for (const k of Object.keys(localStorage)) {
                    if (k.startsWith('lexidy-') && !KEEP.includes(k)) localStorage.removeItem(k);
                  }
                  sessionStorage.clear();
                } catch { /* private mode — the reload is still worth doing */ }
                window.location.assign('/home');
              }}
            >
              Reset this browser
            </button>
            {this.props.onSignOut && (
              <button className="btn btn-sm btn-ghost" onClick={this.props.onSignOut}>Sign out</button>
            )}
          </div>
          <details className="crash-tech">
            <summary>Technical detail</summary>
            <pre>{error.stack || error.message}</pre>
          </details>
        </div>
      </div>
    );
  }
}
